import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShoppingCart, Truck, RotateCcw, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ParticleBackground } from "@/components/ParticleBackground";
import Page from "@/components/layout/Page";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema, ProductSchema } from "@/components/seo/StructuredData";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import { products } from "@/data/products";
import { ProductImagePlaceholder } from "@/components/ProductImagePlaceholder";
import { useState, useMemo } from "react";
import { isBundleEligible } from "@/utils/bundlePricing";

// Format price for display
function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(price);
}

export default function Product() {
  const { handle } = useParams<{ handle: string }>();
  const { addItem } = useCart();
  const { toast } = useToast();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  // Find product from local catalogue
  const product = useMemo(() => {
    return products.find(p => {
      const productHandle = p.slug.replace(/^\/product\//, '').replace(/^\//,'');
      return productHandle === handle || 
             p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') === handle ||
             handle?.includes(productHandle) ||
             productHandle.includes(handle || '');
    });
  }, [handle]);

  const handleAddToCart = () => {
    if (!product) return;
    
    addItem({
      id: product.slug,
      name: product.name,
      slug: product.slug,
      price: product.price,
      image: product.image?.src,
    });
    
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`,
    });
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground />
        <Page>
          <div className="text-center py-32">
            <h1 className="text-2xl font-semibold text-slate-200 mb-4">Product Not Found</h1>
            <p className="text-slate-400 mb-8">The product you're looking for doesn't exist or has been removed.</p>
            <Button asChild>
              <Link to="/shop">Back to Shop</Link>
            </Button>
          </div>
        </Page>
      </div>
    );
  }

  const isAvailable = !product.outOfStock;

  // Build image gallery
  const images: Array<{ src: string; alt: string }> = [];
  if (product.images && product.images.length > 0) {
    images.push(...product.images);
  } else if (product.image) {
    images.push(product.image);
  }

  const currentImage = images[selectedImageIndex] || images[0];

  return (
    <>
      <SEOHead
        title={`${product.name} | Samboards`}
        description={product.description || `Buy ${product.name} - Premium sensory product from Samboards. UK designed and made.`}
        canonical={product.slug}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Shop", url: "/shop" },
          { name: product.name, url: product.slug }
        ]}
      />
      <ProductSchema
        name={product.name}
        description={product.description || ''}
        price={product.price}
        image={currentImage?.src || ''}
        availability={isAvailable ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'}
      />

      <div className="min-h-screen bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground />
        
        <Page>
          <div className="max-w-6xl mx-auto py-6 sm:py-10">
            {/* Back link */}
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-emerald-300 mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Shop
            </Link>

            <div className="grid gap-8 lg:grid-cols-2">
              {/* Image Gallery */}
              <div className="space-y-4">
                {/* Main Image */}
                <div className="aspect-square rounded-2xl border border-slate-700/60 bg-slate-900/70 overflow-hidden">
                  {currentImage ? (
                    <div className="w-full h-full bg-gradient-to-br from-slate-800/60 to-slate-900/80 p-8">
                      <img
                        src={currentImage.src}
                        alt={currentImage.alt}
                        className="w-full h-full object-contain"
                        style={{ 
                          imageRendering: '-webkit-optimize-contrast',
                          backfaceVisibility: 'hidden',
                        }}
                      />
                    </div>
                  ) : (
                    <ProductImagePlaceholder className="w-full h-full" aspectRatio="" />
                  )}
                </div>

                {/* Thumbnails */}
                {images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {images.map((img, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg border overflow-hidden transition-all ${
                          selectedImageIndex === index
                            ? 'border-emerald-400 ring-2 ring-emerald-400/30'
                            : 'border-slate-700/50 hover:border-slate-600'
                        }`}
                      >
                        <img
                          src={img.src}
                          alt={img.alt}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="space-y-6">
                {/* Title & Price */}
                <div>
                  {!isAvailable && (
                    <span className="inline-flex items-center rounded-full border border-slate-400/50 bg-slate-500/20 px-3 py-1 text-xs font-medium uppercase tracking-wider text-slate-300 mb-3">
                      Out of Stock
                    </span>
                  )}
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-emerald-50 leading-tight mb-4">
                    {product.name}
                  </h1>
                  <p className="text-3xl sm:text-4xl font-bold text-emerald-300">
                    {formatPrice(product.price)}
                  </p>
                  {isBundleEligible(product.price, product.name) && (
                    <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-emerald-500/15 border border-emerald-500/30 px-3 py-2">
                      <span className="text-sm font-medium text-emerald-300">
                        Multi-buy: 2 for £10 • 3 for £12
                      </span>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="prose prose-invert prose-sm max-w-none">
                  <p className="text-slate-300 leading-relaxed">
                    {product.description || "Premium sensory product designed in the UK to support focus, calm, and sensory exploration."}
                  </p>
                </div>

                {/* Features (if available) */}
                {product.features && product.features.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">Features</h3>
                    <ul className="space-y-1">
                      {product.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-slate-300">
                          <span className="text-emerald-400">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Add to Cart */}
                <div className="pt-2">
                  <Button
                    onClick={handleAddToCart}
                    size="lg"
                    disabled={!isAvailable}
                    className="w-full sm:w-auto rounded-full bg-gradient-to-r from-emerald-300 to-cyan-300 text-slate-900 font-semibold shadow-[0_0_40px_rgba(45,255,196,0.35)] hover:shadow-[0_0_50px_rgba(45,255,196,0.5)] disabled:opacity-50 disabled:shadow-none px-10 py-6 text-base"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    {isAvailable ? 'Add to Cart' : 'Out of Stock'}
                  </Button>
                </div>

                {/* Trust Row */}
                <div className="flex flex-wrap gap-4 pt-4 border-t border-slate-700/50">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <ShieldCheck className="w-4 h-4 text-emerald-400" />
                    Secure Stripe checkout
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Truck className="w-4 h-4 text-emerald-400" />
                    UK delivery
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <RotateCcw className="w-4 h-4 text-emerald-400" />
                    14-day returns
                  </div>
                </div>

                {/* Policy Links */}
                <div className="flex flex-wrap gap-3 pt-2">
                  <Link to="/delivery" className="text-sm text-emerald-300 hover:text-emerald-200 underline">
                    Delivery info
                  </Link>
                  <span className="text-slate-600">•</span>
                  <Link to="/returns" className="text-sm text-emerald-300 hover:text-emerald-200 underline">
                    Returns policy
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Page>
      </div>
    </>
  );
}
